/**
 * La tabla de ventas de un petshop, igual en «Ventas y rendiciones» y en
 * «Evaluar ventas»: los títulos (se ordena tocándolos), el TOTAL como primera
 * fila, justo arriba de la primera venta (con lo de cada método de pago), y
 * las ventas. Las anuladas se ven tachadas y no suman. Con `anular`, la
 * última columna tiene el botón para anular.
 */
import { useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ArrowDown, ArrowUp, ArrowUpDown, Ban, Banknote, CreditCard, HandCoins, QrCode, Wallet } from 'lucide-react-native';

import { colors, typography } from '@/theme/tokens';
import { formatBs, formatDate } from '@/utils/format';
import type { MetodoPago, PetshopVenta } from '@/types/domain';
import { METODO_LABEL, type Totales } from '@/utils/petshopTotales';

const METODO_ICON: Record<MetodoPago, typeof Banknote> = {
  efectivo: Banknote,
  qr: QrCode,
  transferencia: HandCoins,
  tarjeta: CreditCard,
  otro: Wallet,
};

type Orden = 'fecha' | 'descripcion' | 'metodoPago' | 'monto';

export function TablaVentas({
  ventas,
  totales,
  anular,
}: {
  ventas: PetshopVenta[];
  totales: Totales;
  anular?: { puede: boolean; alTocar: (v: PetshopVenta) => void };
}) {
  const [sortKey, setSortKey] = useState<Orden>('fecha');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const ventasOrdenadas = useMemo(() => {
    const arr = [...ventas];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'fecha':
          cmp = a.fecha.toMillis() - b.fecha.toMillis();
          break;
        case 'descripcion':
          cmp = a.descripcion.localeCompare(b.descripcion);
          break;
        case 'metodoPago':
          cmp = a.metodoPago.localeCompare(b.metodoPago);
          break;
        case 'monto':
          cmp = a.monto - b.monto;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [ventas, sortKey, sortDir]);

  const toggleSort = (k: Orden) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  return (
    <View>
      {/* Header tabla con sort */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'stretch',
          paddingVertical: 10,
          backgroundColor: colors.crema[50],
          borderTopWidth: 1,
          borderBottomWidth: 1,
          borderTopColor: colors.ink[100],
          borderBottomColor: colors.ink[100],
        }}
      >
        <DividedCell width={130} align="center">
          <SortHeader label="Fecha" k="fecha" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} />
        </DividedCell>
        <DividedCell flex={1}>
          <SortHeader label="Producto / descripción" k="descripcion" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} />
        </DividedCell>
        <DividedCell width={140} align="center">
          <SortHeader label="Método" k="metodoPago" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
        </DividedCell>
        <DividedCell width={140} align="center" last={!anular}>
          <SortHeader label="Monto" k="monto" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
        </DividedCell>
        {anular ? (
          <DividedCell width={50} last>
            <Text> </Text>
          </DividedCell>
        ) : null}
      </View>

      {/* TOTAL: la primera fila, justo arriba de la primera venta */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'stretch',
          paddingVertical: 12,
          backgroundColor: colors.magenta[50],
          borderBottomWidth: 1,
          borderBottomColor: colors.magenta[100],
        }}
      >
        <DividedCell width={130} align="center">
          <Text style={{ fontSize: 12, fontWeight: typography.weights.extrabold, color: colors.magenta[700], letterSpacing: 1.4 }}>TOTAL</Text>
        </DividedCell>
        <DividedCell flex={1}>
          <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {totales.ventas} {totales.ventas === 1 ? 'venta' : 'ventas'}
          </Text>
        </DividedCell>
        <DividedCell width={140} align="center">
          {totales.porMetodo.map((m) => (
            <Text key={m.metodo} style={{ fontSize: 12, color: colors.ink[600], textAlign: 'center' }}>
              {m.label} <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900] }}>{formatBs(m.total)}</Text>
            </Text>
          ))}
        </DividedCell>
        <DividedCell width={140} align="center" last={!anular}>
          <Text
            style={{
              textAlign: 'center',
              fontFamily: typography.display.fontFamily,
              fontSize: 18,
              fontWeight: typography.weights.bold,
              color: colors.success[600],
            }}
          >
            {formatBs(totales.total)}
          </Text>
        </DividedCell>
        {anular ? (
          <DividedCell width={50} last>
            <Text> </Text>
          </DividedCell>
        ) : null}
      </View>

      {ventasOrdenadas.map((v, idx) => {
        const metodo = v.metodoPago;
        const Icon = METODO_ICON[metodo];
        const anulada = !!v.anulada;
        return (
          <View
            key={v.id}
            style={{
              flexDirection: 'row',
              alignItems: 'stretch',
              paddingVertical: 12,
              backgroundColor: anulada ? 'rgba(254,242,242,0.6)' : idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
              borderBottomWidth: idx === ventasOrdenadas.length - 1 ? 0 : 1,
              borderBottomColor: colors.ink[100],
              opacity: anulada ? 0.7 : 1,
            }}
          >
            <DividedCell width={130} align="center">
              <View style={{ alignItems: 'center' }}>
                <Text style={{ fontSize: 13, color: colors.ink[800] }}>{formatDate(v.fecha.toDate())}</Text>
                <Text style={{ fontSize: 11, color: colors.ink[500], fontFamily: 'monospace', marginTop: 2 }}>
                  {String(v.fecha.toDate().getHours()).padStart(2, '0')}:{String(v.fecha.toDate().getMinutes()).padStart(2, '0')}
                </Text>
              </View>
            </DividedCell>
            <DividedCell flex={1}>
              <Text
                style={{
                  fontSize: 14,
                  color: anulada ? colors.ink[500] : colors.ink[900],
                  fontWeight: typography.weights.semibold,
                  // @ts-ignore web only
                  textDecorationLine: anulada ? 'line-through' : 'none',
                }}
                numberOfLines={2}
              >
                {v.descripcion}
              </Text>
              {anulada ? (
                <Text style={{ fontSize: 11, color: colors.danger[600], marginTop: 2 }} numberOfLines={2}>
                  ANULADA{v.motivoAnulacion ? ` · ${v.motivoAnulacion}` : ''}{v.anuladaPorNombre ? ` · ${v.anuladaPorNombre}` : ''}
                </Text>
              ) : null}
            </DividedCell>
            <DividedCell width={140} align="center">
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                <Icon size={14} color={colors.ink[500]} />
                <Text style={{ fontSize: 13, color: colors.ink[700] }}>{METODO_LABEL[metodo]}</Text>
              </View>
            </DividedCell>
            <DividedCell width={140} align="center" last={!anular}>
              <Text
                style={{
                  textAlign: 'center',
                  fontFamily: typography.display.fontFamily,
                  fontSize: 16,
                  fontWeight: typography.weights.bold,
                  color: anulada ? colors.ink[400] : colors.success[600],
                  // @ts-ignore web only
                  textDecorationLine: anulada ? 'line-through' : 'none',
                }}
              >
                {formatBs(v.monto)}
              </Text>
            </DividedCell>
            {anular ? (
              <DividedCell width={50} align="center" last>
                {anular.puede && !anulada ? (
                  <Pressable
                    onPress={() => anular.alTocar(v)}
                    hitSlop={6}
                    title="Anular venta"
                    style={({ hovered }) => ({
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: hovered ? colors.danger[50] : 'transparent',
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <Ban size={14} color={colors.danger[600]} />
                  </Pressable>
                ) : null}
              </DividedCell>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

function DividedCell({
  children,
  flex,
  width,
  align = 'left',
  last,
}: {
  children: React.ReactNode;
  flex?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
  last?: boolean;
}) {
  return (
    <View
      style={{
        flex,
        width,
        paddingHorizontal: 14,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'stretch',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[100],
      }}
    >
      {children}
    </View>
  );
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onPress,
  align = 'left',
}: {
  label: string;
  k: Orden;
  sortKey: Orden;
  sortDir: 'asc' | 'desc';
  onPress: (k: Orden) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = sortKey === k;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Pressable
      onPress={() => onPress(k)}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        opacity: hovered ? 0.7 : 1,
        // @ts-ignore web only
        cursor: 'pointer',
        justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      })}
    >
      <Text
        style={{
          fontSize: 11,
          fontWeight: typography.weights.bold,
          color: active ? colors.magenta[700] : colors.ink[500],
          letterSpacing: 1.4,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Text>
      <Icon size={12} color={active ? colors.magenta[700] : colors.ink[400]} />
    </Pressable>
  );
}
